import { Alert, AlertTitle, LinearProgress, Stack } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { RouteTabs } from "../../modules/generic/components/route-tabs";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { ManageDefaultSegmentButton } from "../../modules/segment/components/manager/default-segment-manager";
import { SegmentCrudDataGrid } from "../../modules/segment/components/manager/segment-crud-data-grid";
import { useSegmentSaveAllowed } from "../../modules/segment/hooks/use-segment-save-allowed";
import { useSegmentsWithRelatedMoments } from "../../modules/segment/server/use-segments";
import { usePermissions } from "../../server/auth/use-permissions";
import NotFound from "../not-found";

const SegmentPage: FC<{ is_recipient_type: boolean }> = ({ is_recipient_type }) => {
  const { t } = useTranslation();

  const {
    error,
    loading,
    data: segments,
    reload,
  } = useSegmentsWithRelatedMoments({ is_recipient_type });

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>{t("Something went wrong")}</AlertTitle>
      </Alert>
    );
  }

  if (loading) return <LinearProgress />;

  return (
    <Stack direction="column" spacing={1}>
      {!is_recipient_type && (
        <Stack direction="row-reverse">
          <ManageDefaultSegmentButton />
        </Stack>
      )}
      <SegmentCrudDataGrid
        segments={segments.map((segment) => ({
          ...segment,
          moments: (segment.moments ?? []).filter((x) => x.status !== "archived"),
        }))}
        is_recipient_type={is_recipient_type}
        reload={reload}
      />
    </Stack>
  );
};

const ManageSegmentsPage: FC = () => {
  const { t } = useTranslation();
  const perms = usePermissions();
  const { data: segment_save_allowed, loading } = useSegmentSaveAllowed();

  if (loading) return <LinearProgress />;

  if (!segment_save_allowed) return <NotFound />;

  return (
    <Page title={t("Segments | ChangeEngine")}>
      <PageHeader>
        <PageHeading
          heading={t("Manage your Segments")}
          subheading={t("Add new and manage existing Segments")}
        />
      </PageHeader>
      <PageContent>
        <Stack>
          {perms?.ce_admin ? (
            <RouteTabs
              base="/manage/segments/"
              defaultPage={<SegmentPage is_recipient_type={false} />}
              tabs={[
                {
                  type: "explicit",
                  data: { name: "Segments", route: "standard" },
                  component: <SegmentPage is_recipient_type={false} />,
                },
                {
                  type: "inferred",
                  data: { name: "Audiences" },
                  component: <SegmentPage is_recipient_type={true} />,
                },
              ]}
            />
          ) : (
            <SegmentPage is_recipient_type={false} />
          )}
        </Stack>
      </PageContent>
    </Page>
  );
};

export default ManageSegmentsPage;
